<!-- Единая секция формы: задаёт стандартный заголовок, сетку полей и общий rhythm отступов между блоками. -->
<template>
  <section
    class="base-form-section"
    :class="{
      'base-form-section--collapsible': collapsible,
      'base-form-section--collapsed': collapsible && !isExpanded,
    }"
    @click="toggleCollapsedSection"
  >
    <div
      v-if="collapsible"
      class="form-layout__header form-layout__header--button"
      role="button"
      tabindex="0"
      :aria-expanded="isExpanded.toString()"
      @click.stop="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <span
        class="form-layout__title"
        :class="{ 'form-layout__title--with-icon': icon }"
        :style="titleStyles"
      >
        <slot name="title"><BaseSectionHeader :title="title" :icon="icon" :count="count" /></slot>
        <BaseInfoTooltip v-if="info" :text="info" />
        <AppIcon
          name="chevronUp"
          :size="14"
          class="form-layout__toggle-icon"
          :class="{ 'form-layout__toggle-icon--closed': !isExpanded }"
        />
      </span>
    </div>

    <div v-else-if="!hideTitle" class="form-layout__header">
      <h3
        class="form-layout__title"
        :class="{ 'form-layout__title--with-icon': icon }"
        :style="titleStyles"
      >
        <slot name="title"><BaseSectionHeader :title="title" :icon="icon" :count="count" /></slot>
        <BaseInfoTooltip v-if="info" :text="info" />
      </h3>
    </div>

    <p v-if="collapsible && !isExpanded && summary" class="form-layout__summary">
      {{ summary }}
    </p>

    <div
      v-show="!collapsible || isExpanded"
      class="form-layout__row"
      :class="`form-layout__row--columns-${columns}`"
    >
      <slot />
    </div>
  </section>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";

export default {
  name: "BaseFormSection",

  components: {
    AppIcon,
  },

  props: {
    count: { type: Number, default: null },
    title: {
      type: String,
      default: "",
    },
    columns: {
      type: [Number, String],
      default: 3,
      validator: (value) => ["1", "2", "3"].includes(String(value)),
    },
    icon: {
      type: String,
      default: null,
    },
    info: {
      type: String,
      default: "",
    },
    titleSize: {
      type: [Number, String],
      default: null,
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
    initiallyExpanded: {
      type: Boolean,
      default: true,
    },
    summary: {
      type: String,
      default: "",
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isExpanded: this.initiallyExpanded,
    };
  },

  computed: {
    titleStyles() {
      if (!this.titleSize) return {};

      return {
        fontSize: this.titleSizeValue,
      };
    },

    titleSizeValue() {
      if (typeof this.titleSize === "number") {
        return `${this.titleSize}px`;
      }

      return this.titleSize;
    },
  },

  methods: {
    toggle() {
      this.isExpanded = !this.isExpanded;
    },

    toggleCollapsedSection() {
      if (!this.collapsible || this.isExpanded) return;

      this.isExpanded = true;
    },
  },
};
</script>

<style scoped>
.base-form-section + .base-form-section {
  margin-top: var(--form-section-margin, 16px);
}

.base-form-section--collapsed {
  cursor: pointer;
}

.form-layout__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 9px;
}

.form-layout__header--button {
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.form-layout__header--button:hover .form-layout__title,
.form-layout__header--button:focus-visible .form-layout__title {
  color: var(--color-dark);
}

.form-layout__header--button:focus-visible {
  outline: 2px solid rgba(74, 144, 226, 0.35);
  outline-offset: 4px;
}

.form-layout__title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  line-height: 1.3;
}

.form-layout__title--with-icon {
  gap: 8px;
}

.form-layout__toggle-icon {
  flex: 0 0 auto;
  color: currentColor;
  transition: transform 0.15s ease;
}

.form-layout__toggle-icon--closed {
  transform: rotate(180deg);
}

.form-layout__row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--form-section-gap) 16px;
  align-items: start;
}

.form-layout__summary {
  min-width: 0;
  margin: -2px 0 0;
  overflow: hidden;
  color: #637381;
  font-size: 13px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.form-layout__row--columns-1 {
  grid-template-columns: 1fr;
}

.form-layout__row--columns-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.form-layout__row--columns-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.form-layout__row :slotted(.form-group) {
  margin-bottom: 0;
}

:global(.form-layout--compact) .form-layout__row {
  grid-template-columns: 1fr;
}

:global(.form-layout--compact) .form-layout__row :slotted(.form-layout__field--span-2) {
  grid-column: span 1;
}

@media screen and (max-width: 1023px) {
  .form-layout__row--columns-3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 767px) {
  .form-layout__row {
    grid-template-columns: 1fr;
  }
}
</style>
